import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { StudentInfo } from '@/types/schedule';

export type AdminOccurrenceEditRow = {
  occurrence_id: string;
  fixed_job_id: string;
  occurrence_date: string; // YYYY-MM-DD
  start_time: string | null;
  end_time: string | null;
  effective_duration_minutes: number | null;
  status: 'SCHEDULED' | 'TEACHER_LEAVE' | 'STUDENT_LEAVE' | 'STUDENT_ABSENT';
  edit_status: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED';
  edit_request_payload: Record<string, unknown> | null;
  edit_reviewed_by: string | null;
  edit_reviewed_at: string | null;
  teacher_code: string;
  subject: string;
  location: string;
  default_duration_minutes: number;
  students: StudentInfo[];
};

export type AdminOccurrenceEditsParams = {
  page: number;
  itemsPerPage: number;
  searchQuery: string;
  statusFilter: 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED';
  sortField: 'occurrence_date' | 'teacher_code' | 'subject' | 'status' | 'edit_status';
  sortDirection: 'asc' | 'desc';
  riskOnly: boolean;
};

export type RiskMap = Record<string, boolean | undefined>;

export function useAdminOccurrenceEdits(params: AdminOccurrenceEditsParams) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['admin-occurrence-edits', params],
    queryFn: async () => {
      const from = (params.page - 1) * params.itemsPerPage;
      const to = from + params.itemsPerPage - 1;

      // Build base query with inner join to fixed_jobs for teacher_code/subject/location
      // Use view with students joined for richer context
      let q = (supabase as any)
        .from('job_occurrence_with_students')
        .select(
          [
            'occurrence_id',
            'fixed_job_id',
            'occurrence_date',
            'start_time',
            'end_time',
            'effective_duration_minutes',
            'status',
            'edit_status',
            'edit_request_payload',
            'edit_reviewed_by',
            'edit_reviewed_at',
            'teacher_code',
            'subject',
            'location',
            'default_duration_minutes',
            'students',
          ].join(', '),
          { count: 'exact' }
        );

      // Filter: only rows that ever had edits or currently pending; for admin page default show all with non-NONE
      if (params.statusFilter !== 'ALL') {
        q = q.eq('edit_status', params.statusFilter);
      } else {
        q = q.neq('edit_status', 'NONE');
      }

      // Search on teacher_code/subject/location
      if (params.searchQuery.trim()) {
        const term = `%${params.searchQuery.trim()}%`;
        q = q.or(['teacher_code.ilike.' + term, 'subject.ilike.' + term, 'location.ilike.' + term].join(','));
      }

      // Sorting
      const ascending = params.sortDirection === 'asc';
      q = q.order(params.sortField, { ascending });

      const { data, error, count } = await q.range(from, to);
      if (error) throw error;

      const rows = (data ?? []).map((r: any) => {
        return {
          occurrence_id: r.occurrence_id as string,
          fixed_job_id: r.fixed_job_id as string,
          occurrence_date: r.occurrence_date as string,
          start_time: r.start_time as string | null,
          end_time: r.end_time as string | null,
          effective_duration_minutes: r.effective_duration_minutes as number | null,
          status: r.status,
          edit_status: r.edit_status,
          edit_request_payload: (r.edit_request_payload ?? null) as Record<string, unknown> | null,
          edit_reviewed_by: r.edit_reviewed_by as string | null,
          edit_reviewed_at: r.edit_reviewed_at as string | null,
          teacher_code: r.teacher_code as string,
          subject: r.subject as string,
          location: r.location as string,
          default_duration_minutes: r.default_duration_minutes as number,
          students: r.students as StudentInfo[],
        } as AdminOccurrenceEditRow;
      }) as AdminOccurrenceEditRow[];

      const total = count ?? rows.length;
      return { rows, total };
    },
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  // Risk calculation per occurrence using students[] fields
  const [riskMap, setRiskMap] = useState<RiskMap>({});
  const recomputeRisks = useCallback(async (rows: AdminOccurrenceEditRow[]) => {
    const entries = rows.map((r) => {
      const students = Array.isArray(r.students) ? r.students : [];
      const insufficient = students.some((s) => {
        const hours = typeof s.hours === 'number' ? s.hours : 0;
        const holdMinutes = typeof s.active_hold_minutes === 'number' ? s.active_hold_minutes : 0;
        const availableMinutes = Math.round(hours * 60) - holdMinutes;
        return availableMinutes < 0;
      });
      return [r.occurrence_id, insufficient] as const;
    });
    const map: RiskMap = {};
    for (const [id, flag] of entries) map[id] = flag;
    setRiskMap(map);
  }, []);

  const filteredRows = useMemo(() => {
    const base = query.data?.rows ?? [];
    if (!params.riskOnly) return base;
    return base.filter((r) => riskMap[r.occurrence_id] === true);
  }, [query.data, params.riskOnly, riskMap]);

  const approveReject = useMutation({
    mutationFn: async (p: { occurrenceId: string; reviewer: string; decision: 'APPROVE' | 'REJECT'; reason?: string }) => {
      // Try 4-arg version first; if ambiguous (PGRST203), retry with 5th arg _request_id: null for overloaded DBs
      const tryCall = async (args: Record<string, unknown>, useWrapper: boolean) => {
        const { data, error } = await (supabase as any).rpc(useWrapper ? 'review_occurrence_edit_client' : 'review_occurrence_edit', args);
        if (error) throw error;
        return data;
      };
      try {
        return await tryCall({
          _occurrence_id: p.occurrenceId,
          _reviewer: p.reviewer,
          _decision: p.decision,
          _reason: p.reason ?? null,
        }, true);
      } catch (e: any) {
        const code = e?.code || e?.data?.code;
        if (code === 'PGRST203' || code === 'PGRST202' || e?.message?.includes('Could not choose the best candidate') || e?.message?.includes('Could not find the function')) {
          return await tryCall({
            _occurrence_id: p.occurrenceId,
            _reviewer: p.reviewer,
            _decision: p.decision,
            _reason: p.reason ?? null,
            _request_id: null,
          }, false);
        }
        throw e;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-occurrence-edits'] }),
  });

  const overrideEdit = useMutation({
    mutationFn: async (p: { occurrenceId: string; reviewer: string; payload: Record<string, unknown> }) => {
      // Write payload then approve via review RPC to trigger all recalc rules
      const { error: upErr } = await (supabase as any)
        .from('job_occurrences')
        .update({ edit_request_payload: p.payload })
        .eq('id', p.occurrenceId);
      if (upErr) throw upErr;
      // Same overload-safe call as above
      const tryCall = async (args: Record<string, unknown>, useWrapper: boolean) => {
        const { data, error } = await (supabase as any).rpc(useWrapper ? 'review_occurrence_edit_client' : 'review_occurrence_edit', args);
        if (error) throw error;
        return data;
      };
      const reasonFromPayload = typeof (p.payload as any)?.note === 'string' && (p.payload as any).note!.trim() !== '' ? (p.payload as any).note : null;
      try {
        return await tryCall({
          _occurrence_id: p.occurrenceId,
          _reviewer: p.reviewer,
          _decision: 'APPROVE',
          _reason: reasonFromPayload,
        }, true);
      } catch (e: any) {
        const code = e?.code || e?.data?.code;
        if (code === 'PGRST203' || code === 'PGRST202' || e?.message?.includes('Could not choose the best candidate') || e?.message?.includes('Could not find the function')) {
          return await tryCall({
            _occurrence_id: p.occurrenceId,
            _reviewer: p.reviewer,
            _decision: 'APPROVE',
            _reason: reasonFromPayload,
            _request_id: null,
          }, false);
        }
        throw e;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-occurrence-edits'] }),
  });

  return {
    query,
    filteredRows,
    total: query.data?.total ?? 0,
    riskMap,
    recomputeRisks,
    approveReject,
    overrideEdit,
  };
}



